import React, { useState, useRef, useEffect, useContext } from "react";
import "./AgendaControls.css";
import {
  LuChevronDown,
  LuCalendarDays,
  LuLayoutGrid,
  LuCheck,
  LuBookmark,
  LuCheckCircle2,
  LuUser,
  LuUsers,
  LuSearch,
  LuX,
} from "react-icons/lu";
import {
  AGENDA_CATEGORY_LIST,
  EXPLORE_CATEGORY_LIST,
} from "../../constants/agendaConstants";
import { Language } from "../../App";

export default function AgendaControls({
  viewMode,
  setViewMode,
  selectedCategory,
  setSelectedCategory,
  searchTerm: externalSearchTerm,
  setSearchTerm: externalSetSearchTerm,
}) {
  const { lang } = useContext(Language) || {};
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");

  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const updateSearchTerm = (val) => {
    if (typeof externalSetSearchTerm === "function") {
      externalSetSearchTerm(val);
    } else {
      setInternalSearchTerm(val);
    }
  };

  const filterRef = useRef(null);
  const viewDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(e.target)) {
        setViewDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (viewMode === "saved" && selectedCategory === "mentorship") {
      setSelectedCategory("all");
    } else if (
      viewMode === "explore" &&
      (selectedCategory === "clinical_session" || selectedCategory === "mentorship")
    ) {
      setSelectedCategory("all");
    }
  }, [viewMode, selectedCategory, setSelectedCategory]);

  const categoriesList =
    viewMode === "saved"
      ? AGENDA_CATEGORY_LIST.filter((cat) => cat.id !== "mentorship")
      : viewMode === "schedules" || viewMode === "completed"
      ? AGENDA_CATEGORY_LIST
      : EXPLORE_CATEGORY_LIST;

  const getFilterLabel = (cat) => {
    if (!cat) return lang?.all_types || "All Types";
    const id = typeof cat === "string" ? cat : cat.id;
    switch (id) {
      case "all":
        return lang?.all_types || "All Types";
      case "event":
        return lang?.events || "Events";
      case "webinar":
        return lang?.webinars || "Webinars";
      case "clinical_session":
        return lang?.clinical_sessions || "Clinical Sessions";
      case "mentorship":
        return lang?.mentorships || "Mentorships";
      default:
        return cat.filterLabel || cat.label || id;
    }
  };

  const currentCategoryObj = categoriesList.find((c) => c.id === selectedCategory);
  const currentLabel = getFilterLabel(currentCategoryObj || selectedCategory);

  const handleTabChange = (mode) => {
    if (viewMode === mode) return;
    if (typeof setViewMode === "function") {
      setViewMode(mode);
    }
  };

  const handleOptionSelect = (id) => {
    setViewDropdownOpen(false);
    handleTabChange(id);
  };

  const viewOptions = [
    {
      id: "explore",
      label: lang?.all_agendas || "All Agendas",
      icon: LuLayoutGrid,
    },
    {
      id: "saved",
      label: lang?.saved_agendas || "Saved Agendas",
      icon: LuBookmark,
    },
    {
      id: "completed",
      label: lang?.completed_agendas || "Completed Agendas",
      icon: LuCheckCircle2,
    },
  ];

  const isDropdownActive = viewMode !== "schedules";

  let currentViewLabel = lang?.all_agendas || "All Agendas";
  let ActiveDropdownIcon = LuLayoutGrid;

  if (viewMode === "saved") {
    currentViewLabel = lang?.saved_agendas || "Saved Agendas";
    ActiveDropdownIcon = LuBookmark;
  } else if (viewMode === "completed") {
    currentViewLabel = lang?.completed_agendas || "Completed Agendas";
    ActiveDropdownIcon = LuCheckCircle2;
  } else {
    currentViewLabel = lang?.all_agendas || "All Agendas";
    ActiveDropdownIcon = LuLayoutGrid;
  }



  return (
    <nav className="agenda-controls-bar" aria-label="Agenda view and category filters" role="navigation">
      {/* Left controls: Agenda view switcher */}
      <div className="agenda-controls-left">
        <div className="agenda-view-switcher" role="tablist" aria-label="Agenda view switcher">
          {/* All Agendas Dropdown Pill Button */}
          <div className="agenda-view-dropdown-wrapper" ref={viewDropdownRef}>
            <button
              id="agenda-tab-all-agendas-dropdown"
              type="button"
              role="tab"
              aria-selected={isDropdownActive}
              aria-haspopup="listbox"
              aria-expanded={viewDropdownOpen}
              aria-label={currentViewLabel}
              className={`agenda-view-tab-btn agenda-view-dropdown-btn ${isDropdownActive ? "active" : ""}`}
              onClick={() => setViewDropdownOpen((prev) => !prev)}
            >
              <span className="agenda-view-btn-content">
                <ActiveDropdownIcon size={15} />
                <span>{currentViewLabel}</span>
              </span>
              <LuChevronDown
                size={15}
                className={`agenda-dropdown-arrow ${viewDropdownOpen ? "open" : ""}`}
              />
            </button>

            {viewDropdownOpen && (
              <div
                className="agenda-view-dropdown-menu agenda-filter-menu"
                role="listbox"
                aria-label="Select agenda view"
              >
                {viewOptions.map((opt) => {
                  const OptIcon = opt.icon;
                  const isSelected = viewMode === opt.id;
                  return (
                    <div
                      key={opt.id}
                      id={`agenda-view-item-${opt.id}`}
                      role="option"
                      aria-selected={isSelected}
                      className={`agenda-filter-item ${isSelected ? "selected" : ""}`}
                      onClick={() => handleOptionSelect(opt.id)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <OptIcon size={14} />
                        <span>{opt.label}</span>
                      </div>
                      {isSelected && <LuCheck size={14} color="#00BADA" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Schedules Tab Button */}
          <button
            id="agenda-tab-schedules"
            role="tab"
            aria-selected={viewMode === "schedules"}
            aria-current={viewMode === "schedules" ? "page" : undefined}
            aria-controls="agenda-view-content"
            className={`agenda-view-tab-btn ${viewMode === "schedules" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("schedules");
            }}
            data-route="/agenda/schedules"
          >
            <LuCalendarDays size={15} />
            <span>{lang?.schedules || "Schedules"}</span>
          </button>
        </div>
      </div>

      {/* Right controls: Search input, Sort dropdown, and Types dropdown filter */}
      <div className="agenda-controls-right">
        {/* Search Input Box (commented out for now) */}
        {/*
        <div className="agenda-search-box-wrapper">
          <LuSearch size={16} className="agenda-search-icon" aria-hidden="true" />
          <input
            id="agenda-search-input"
            type="text"
            className="agenda-search-input"
            placeholder={lang?.search_agendas_placeholder || "Search agendas, events, webinars..."}
            value={searchTerm}
            onChange={(e) => updateSearchTerm(e.target.value)}
            aria-label="Search agendas"
          />
          {searchTerm && (
            <button
              type="button"
              className="agenda-search-clear-btn"
              onClick={() => updateSearchTerm("")}
              aria-label="Clear search"
            >
              <LuX size={14} />
            </button>
          )}
        </div>
        */}

        {/* Types Filter Dropdown */}
        <div className="agenda-filter-dropdown-wrapper" ref={filterRef}>
          <button
            id="agenda-filter-btn"
            className="agenda-filter-btn"
            onClick={() => setFilterOpen((prev) => !prev)}
            aria-expanded={filterOpen}
            aria-haspopup="listbox"
            aria-label="Filter events by category"
          >
            <span>{currentLabel}</span>
            <LuChevronDown size={15} />
          </button>

          {filterOpen && (
            <div className="agenda-filter-menu" role="listbox" aria-label="Select category filter">
              {categoriesList.map((cat) => (
                <div
                  key={cat.id}
                  id={`agenda-filter-item-${cat.id}`}
                  role="option"
                  aria-selected={selectedCategory === cat.id}
                  className={`agenda-filter-item ${selectedCategory === cat.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setFilterOpen(false);
                  }}
                >
                  <span>{getFilterLabel(cat)}</span>
                  {selectedCategory === cat.id && <LuCheck size={14} color="#00BADA" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}